import { useTranslation } from "react-i18next";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SchemaOrg, getBreadcrumbSchema, getOrganizationSchema, getWebSiteSchema, getOnlineBusinessSchema } from "@/components/seo/SchemaOrg";
import { HreflangTags } from "@/components/seo/HreflangTags";
import { getLocalizedUrl } from "@/hooks/useSeoMeta";
import { LocaleKey } from "@/i18n";
import { 
  CreditCard,
  Globe,
  Headphones,
  MapPin,
  Building2,
  Users,
  Star,
  Heart
} from "lucide-react";
import { useLocale } from "@/contexts/LocaleContext";

const AboutUs = () => {
  const { t } = useTranslation();
  const { locale } = useLocale();

  const baseUrl = getLocalizedUrl(locale as LocaleKey, '/');

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: t('nav.home', 'Home'), url: baseUrl },
    { name: t('footer.aboutUs'), url: getLocalizedUrl(locale as LocaleKey, '/quienes-somos') }
  ]);

  const organizationSchema = getOrganizationSchema(locale as LocaleKey);
  const webSiteSchema = getWebSiteSchema(locale as LocaleKey);
  const onlineBusinessSchema = getOnlineBusinessSchema(locale as LocaleKey);

  const aboutPageSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": t('aboutUs.pageTitle'),
    "description": t('aboutUs.pageDescription'),
    "url": getLocalizedUrl(locale as LocaleKey, '/quienes-somos'),
    "mainEntity": {
      "@type": "Organization",
      "@id": `${baseUrl}#organization`,
      "name": "Fora da Rota",
      "description": t('aboutUs.pageDescription'),
      "url": baseUrl,
      "foundingDate": "2024",
      "slogan": t('aboutUs.heroTitle')
    }
  };

  const features = [
    {
      icon: CreditCard,
      title: t('aboutUs.features.prices.title'),
      description: t('aboutUs.features.prices.description')
    },
    {
      icon: Globe,
      title: t('aboutUs.features.online.title'),
      description: t('aboutUs.features.online.description')
    },
    {
      icon: Headphones,
      title: t('aboutUs.features.support.title'),
      description: t('aboutUs.features.support.description')
    }
  ];

  const stats = [
    { value: "500+", label: t('aboutUs.stats.hotels', 'Hotéis'), icon: Building2 },
    { value: "50+", label: t('aboutUs.stats.destinations', 'Destinos'), icon: MapPin },
    { value: "10k+", label: t('aboutUs.stats.customers', 'Clientes Felizes'), icon: Users },
    { value: "4.9", label: t('aboutUs.stats.rating', 'Avaliação'), icon: Star }
  ];

  return (
    <>
      <HreflangTags path="/quienes-somos" />
<SchemaOrg schema={[organizationSchema, webSiteSchema, onlineBusinessSchema, breadcrumbSchema, aboutPageSchema]} />

      <div className="min-h-screen bg-background overflow-x-hidden">
        <Navbar />
        
        {/* Hero Section with Background */}
        <section className="relative h-[50vh] min-h-[400px] flex items-center justify-center overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=800&q=60"
            srcSet="https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=640&q=60 640w, https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=800&q=60 800w, https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=1280&q=50 1280w, https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=1920&q=40 1920w"
            sizes="100vw"
            alt=""
            role="presentation"
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60" />
          <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
              {t('aboutUs.heroTitle')}
            </h1>
            <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto">
              {t('aboutUs.heroSubtitle')}
            </p>
          </div>
        </section>

        <main id="main-content" className="container mx-auto px-4">
          {/* Why Choose Us - Feature Cards */}
          <section className="py-16 -mt-16 relative z-20">
            <div className="grid md:grid-cols-3 gap-6">
              {features.map((feature, index) => (
                <div 
                  key={index} 
                  className="bg-card rounded-xl p-8 shadow-lg border border-border/50 text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-5">
                    <feature.icon className="h-8 w-8 text-primary" />
                  </div>
                  <h2 className="text-xl font-semibold text-foreground mb-3">{feature.title}</h2>
                  <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* About Section with Image */}
          <section className="py-16">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                  {t('aboutUs.travelersTitle')}
                </h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p>{t('aboutUs.travelersText1')}</p>
                  <p>{t('aboutUs.travelersText2')}</p>
                  <p className="font-medium text-foreground flex items-center gap-2">
                    <Heart className="h-5 w-5 text-primary" />
                    {t('aboutUs.travelersConclusion')}
                  </p>
                </div>
              </div>
              <div className="relative overflow-visible">
                <div className="rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=800&q=80"
                    alt="Paisagem de viagem"
                    className="w-full h-[400px] object-cover"
                  />
                </div>
                {/* Decorative element */}
                <div className="hidden lg:block absolute -bottom-6 -left-6 w-32 h-32 bg-primary/10 rounded-2xl -z-10" />
                <div className="hidden lg:block absolute -top-6 -right-6 w-24 h-24 bg-primary/20 rounded-full -z-10" />
              </div>
            </div>
          </section>

          {/* Stats Counter Section */}
          <section className="py-16">
            <div className="bg-primary rounded-2xl p-8 md:p-12">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white/20 mb-4">
                      <stat.icon className="h-6 w-6 text-white" />
                    </div>
                    <div className="text-3xl md:text-4xl font-bold text-white mb-2">{stat.value}</div>
                    <div className="text-white/80 text-sm md:text-base">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Purpose Section */}
          <section className="py-16">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1 relative">
                <div className="rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://images.unsplash.com/photo-1530789253388-582c481c54b0?w=800&q=80"
                    alt="Viagem e aventura"
                    className="w-full h-[400px] object-cover"
                  />
                </div>
                <div className="hidden lg:block absolute -bottom-6 -right-6 w-32 h-32 bg-primary/10 rounded-2xl -z-10" />
              </div>
              <div className="order-1 lg:order-2">
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                  {t('aboutUs.purposeTitle')}
                </h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p>{t('aboutUs.purposeText1')}</p>
                  <p>{t('aboutUs.purposeText2')}</p>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-16">
            <div className="bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 rounded-2xl p-8 md:p-12 text-center border border-primary/20">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                {t('aboutUs.ctaTitle', 'Pronto para sua próxima aventura?')}
              </h2>
              <p className="text-muted-foreground mb-8 max-w-2xl mx-auto leading-relaxed">
                {t('aboutUs.ctaText')}
              </p>
              <div className="flex items-center justify-center gap-3">
                <MapPin className="h-6 w-6 text-primary" />
                <div className="text-left">
                  <p className="font-bold text-foreground">{t('aboutUs.teamName')}</p>
                  <p className="text-sm text-muted-foreground italic">
                    {t('aboutUs.teamSlogan')}
                  </p>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default AboutUs;